import React, { useEffect, useState } from "react";
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../../firebase/config";

const PASSWORD_SYMBOLS = "^$*.[]{}()?\"!@#%&/\\,><':;|_~`-";

const COPY = {
  en: {
    resetTitle: "Reset your password",
    resetLead: "Choose a new password for this sign-in.",
    emailLabel: "Sign-in",
    password: "New password",
    confirm: "Confirm password",
    rules: "Use 8 to 25 characters, with an uppercase letter, a lowercase letter, a number, and a symbol.",
    save: "Save password",
    saving: "Saving",
    saved: "The password is saved. You can sign in.",
    signIn: "Sign in",
    mismatch: "The passwords do not match.",
    weak: "The password does not meet the requirements.",
    expired: "This link has expired or has already been used. Request a new email.",
    verifyTitle: "Confirm your email address",
    verifyLead: "Confirm this address to finish the sign-in setup.",
    verifyButton: "Confirm email address",
    verified: "The email address is confirmed. You can sign in.",
    recoverTitle: "Restore the previous email address",
    recoverLead: "This returns the sign-in to the previous email address.",
    recoverButton: "Restore email address",
    recovered: "The previous email address is restored. You can sign in.",
  },
  fr: {
    resetTitle: "Réinitialisez votre mot de passe",
    resetLead: "Choisissez un nouveau mot de passe pour cette connexion.",
    emailLabel: "Connexion",
    password: "Nouveau mot de passe",
    confirm: "Confirmez le mot de passe",
    rules: "Utilisez 8 à 25 caractères, avec une majuscule, une minuscule, un chiffre et un symbole.",
    save: "Enregistrer le mot de passe",
    saving: "Enregistrement",
    saved: "Le mot de passe est enregistré. Vous pouvez vous connecter.",
    signIn: "Connexion",
    mismatch: "Les mots de passe ne correspondent pas.",
    weak: "Le mot de passe ne respecte pas les exigences.",
    expired: "Ce lien a expiré ou a déjà été utilisé. Demandez un nouveau courriel.",
    verifyTitle: "Confirmez votre adresse courriel",
    verifyLead: "Confirmez cette adresse pour terminer la connexion.",
    verifyButton: "Confirmer l'adresse courriel",
    verified: "L'adresse courriel est confirmée. Vous pouvez vous connecter.",
    recoverTitle: "Rétablir l'adresse courriel précédente",
    recoverLead: "Ceci ramène la connexion à l'adresse courriel précédente.",
    recoverButton: "Rétablir l'adresse courriel",
    recovered: "L'adresse courriel précédente est rétablie. Vous pouvez vous connecter.",
  },
};

function passwordIsValid(password) {
  const value = String(password || "");
  const symbols = new Set(PASSWORD_SYMBOLS.split(""));
  let hasSymbol = false;
  let hasUnsupported = false;
  for (const char of value) {
    if (/[A-Za-z0-9]/.test(char)) continue;
    if (symbols.has(char)) hasSymbol = true;
    else hasUnsupported = true;
  }
  return value.length >= 8 && value.length <= 25 && /[A-Z]/.test(value) && /[a-z]/.test(value) && /[0-9]/.test(value) && hasSymbol && !hasUnsupported;
}

function expiredCode(error) {
  const code = String(error?.code || "");
  return code === "auth/expired-action-code" || code === "auth/invalid-action-code" || code === "auth/user-disabled" || code === "auth/user-not-found";
}

export default function AuthActionPage() {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode") || "";
  const oobCode = params.get("oobCode") || "";
  const language = String(params.get("lang") || "").toLowerCase().startsWith("fr") ? "fr" : "en";
  const copy = COPY[language];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(mode !== "resetPassword");

  useEffect(() => {
    if (mode !== "resetPassword" || !oobCode) {
      if (!oobCode) setError(copy.expired);
      setReady(true);
      return undefined;
    }
    let active = true;
    verifyPasswordResetCode(auth, oobCode)
      .then((account) => {
        if (active) setEmail(account);
      })
      .catch(() => {
        if (active) setError(copy.expired);
      })
      .finally(() => {
        if (active) setReady(true);
      });
    return () => {
      active = false;
    };
  }, [mode, oobCode, copy.expired]);

  const savePassword = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!passwordIsValid(password)) {
      setError(copy.weak);
      return;
    }
    if (password !== confirm) {
      setError(copy.mismatch);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(copy.saved);
    } catch (err) {
      const code = String(err?.code || "");
      setError(code === "auth/password-does-not-meet-requirements" || code === "auth/weak-password" ? copy.weak : copy.expired);
    } finally {
      setBusy(false);
    }
  };

  const confirmEmail = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await applyActionCode(auth, oobCode);
      setDone(mode === "recoverEmail" ? copy.recovered : copy.verified);
    } catch (err) {
      setError(expiredCode(err) ? copy.expired : copy.expired);
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "verifyEmail" ? copy.verifyTitle : mode === "recoverEmail" ? copy.recoverTitle : copy.resetTitle;
  const lead = mode === "verifyEmail" ? copy.verifyLead : mode === "recoverEmail" ? copy.recoverLead : copy.resetLead;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-md rounded-xl border-t-4 border-sky-500 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
        <p className="mt-2 text-sm text-gray-600">{lead}</p>
        {email ? <p className="mt-3 text-sm font-semibold text-gray-800">{copy.emailLabel}: {email}</p> : null}
        {error ? <p className="mt-3 text-sm font-medium text-red-600">{error}</p> : null}
        {done ? (
          <>
            <p className="mt-4 text-sm text-gray-800">{done}</p>
            <a href="https://pos.nuagepos.com/#/login" className="mt-4 flex h-16 w-full items-center justify-center rounded-md bg-sky-500 text-xl font-bold text-white">
              {copy.signIn}
            </a>
          </>
        ) : null}
        {!done && ready && mode === "resetPassword" && !error ? (
          <form className="mt-4 flex flex-col gap-3" onSubmit={savePassword}>
            <label className="text-sm font-semibold text-gray-800" htmlFor="new-password">{copy.password}</label>
            <input id="new-password" type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} className="h-12 rounded-md border border-gray-300 px-3 text-base" />
            <label className="text-sm font-semibold text-gray-800" htmlFor="confirm-password">{copy.confirm}</label>
            <input id="confirm-password" type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} className="h-12 rounded-md border border-gray-300 px-3 text-base" />
            <p className="text-sm text-gray-600">{copy.rules}</p>
            <button type="submit" disabled={busy} className="h-16 w-full rounded-md bg-sky-500 text-xl font-bold text-white">
              {busy ? copy.saving : copy.save}
            </button>
          </form>
        ) : null}
        {!done && ready && (mode === "verifyEmail" || mode === "recoverEmail") && !error ? (
          <button type="button" disabled={busy} onClick={confirmEmail} className="mt-4 h-16 w-full rounded-md bg-sky-500 text-xl font-bold text-white">
            {busy ? copy.saving : mode === "recoverEmail" ? copy.recoverButton : copy.verifyButton}
          </button>
        ) : null}
      </div>
    </div>
  );
}
